import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { vaultPosPhrase } from '@il-colpo/engine';
import { Badge, Button } from '../../ui/index.ts';
import { useGame } from '../GameContext.tsx';
import { SYMBOL_GLYPHS } from '../text/phraseText.ts';
import { useLooseT } from '../text/loose.ts';
import { VaultObject } from './objects/VaultObject.tsx';
import { ClueLine, HeardList, type PuzzleProps } from './shared.tsx';
import styles from './puzzles.module.css';

interface VaultView {
  length: number;
  alphabet: number;
  entered: (number | null)[];
  confirms: string[];
  errors: number;
  locked: boolean;
  mySymbols: { pos: number; symbol: number }[];
}

const HEARD = ['vaultPos', 'vaultConfirm'] as const;
const MAX_ERRORS = 3;

/** Vault code (GDD §8.9): five symbols, each known to someone; everybody confirms the code. */
export function VaultPanel({ puzzleId, v: raw, lens = false }: PuzzleProps) {
  const { t } = useTranslation('game');
  const { view, act, say } = useGame();
  const names = useLooseT('phrases');
  const v = raw as unknown as VaultView;
  const [slot, setSlot] = useState(0);
  const full = v.entered.every((s) => s !== null);
  const confirmed = v.confirms.includes(view.you.id);
  const own = new Set(v.mySymbols.map((m) => m.pos));

  return (
    <div className={styles['stack']}>
      {lens ? null : (
        <VaultObject
          filled={v.entered.filter((s) => s !== null).length}
          length={v.length}
          locked={v.locked}
          confirmed={v.confirms.length}
          everyone={view.players.filter((p) => !p.expelled).length}
        />
      )}
      {lens ? null : (
        <p className={styles['progress']}>
          {t('puzzle.vault.errors', { n: v.errors, max: MAX_ERRORS })}
          {v.locked ? ` · ${t('puzzle.vault.locked')}` : ''}
        </p>
      )}
      <section aria-label={lens ? t('puzzle.vault.theirs') : t('puzzle.vault.mine')}>
        <h4 className={styles['heardTitle']}>
          {lens ? t('puzzle.vault.theirs') : t('puzzle.vault.mine')}
        </h4>
        <ul className={styles['clues']}>
          {v.mySymbols.map((m) => (
            <ClueLine key={m.pos} phrase={vaultPosPhrase(m.pos, m.symbol)} />
          ))}
        </ul>
      </section>
      {lens ? null : (
        <>
          <div className={styles['slots']} role="group" aria-label={t('puzzle.vault.code')}>
            {v.entered.map((s, i) => (
              <button
                key={i}
                type="button"
                className={[
                  styles['slot'],
                  slot === i ? styles['selected'] : '',
                  own.has(i) ? styles['own'] : '',
                ].join(' ')}
                aria-pressed={slot === i}
                aria-label={t('puzzle.vault.slot', {
                  n: i + 1,
                  symbol: s === null ? t('puzzle.vault.empty') : names(`symbols.${s}`),
                })}
                onClick={() => setSlot(i)}
              >
                <span className={styles['slotNumber']}>{i + 1}</span>
                <span className={styles['glyph']}>{s === null ? '·' : SYMBOL_GLYPHS[s]}</span>
              </button>
            ))}
          </div>
          <div className={styles['symbols']} role="group" aria-label={t('puzzle.vault.symbols')}>
            {Array.from({ length: v.alphabet }, (_, s) => (
              <button
                key={s}
                type="button"
                className={styles['symbol']}
                disabled={v.locked}
                aria-label={names(`symbols.${s}`)}
                onClick={() => {
                  act(puzzleId, { kind: 'vault.set', pos: slot, symbol: s });
                  setSlot((cur) => Math.min(v.length - 1, cur + 1));
                }}
              >
                <span className={styles['glyph']}>{SYMBOL_GLYPHS[s]}</span>
              </button>
            ))}
          </div>
          <div className={styles['row']}>
            <Button
              disabled={!full || v.locked}
              variant={confirmed ? 'secondary' : 'primary'}
              onClick={() =>
                act(puzzleId, confirmed ? { kind: 'vault.unconfirm' } : { kind: 'vault.confirm' })
              }
            >
              {confirmed ? t('puzzle.vault.unconfirm') : t('puzzle.vault.confirm')}
            </Button>
            <Button
              variant="ghost"
              icon="chat"
              onClick={() => say({ id: 'vaultConfirm', params: [] })}
            >
              {t('puzzle.vault.sayConfirm')}
            </Button>
          </div>
          <ul className={styles['confirms']}>
            {view.players
              .filter((p) => !p.expelled)
              .map((p) => (
                <li key={p.id}>
                  {p.name}{' '}
                  <Badge tone={v.confirms.includes(p.id) ? 'green' : 'neutral'}>
                    {v.confirms.includes(p.id) ? t('puzzle.vault.in') : t('puzzle.vault.waiting')}
                  </Badge>
                </li>
              ))}
          </ul>
          <HeardList puzzleId={puzzleId} ids={HEARD} />
        </>
      )}
    </div>
  );
}
